import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Moon, Sun, Battery, Heart, Loader2, TrendingUp, AlertTriangle, CheckCircle, Smile, Frown } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function RecoveryTracker({ childId }) {
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(null);
  const [history, setHistory] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [sleepData, setSleepData] = useState({ sleep_duration_hours: 8, sleep_quality: 7 });
  const [wellnessData, setWellnessData] = useState({ energy_level: 7, mood: 7, muscle_soreness: 3, stress_level: 3, hydration: 7 });

  const fetchData = useCallback(async () => {
    try {
      const [scoreRes, historyRes] = await Promise.all([
        axios.get(`${API}/recovery/score/${childId}`),
        axios.get(`${API}/recovery/history/${childId}?days=7`)
      ]);
      setScore(scoreRes.data);
      setHistory(historyRes.data.history);
    } catch (err) {
      console.error('Failed to fetch recovery data');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    if (childId) fetchData();
  }, [childId, fetchData]);

  const logSleep = async () => {
    try {
      await axios.post(`${API}/recovery/sleep`, {
        child_id: childId,
        date: new Date().toISOString().split('T')[0],
        ...sleepData
      });
      toast.success('Sleep logged!');
      fetchData();
    } catch (err) {
      toast.error('Failed to log sleep');
    }
  };

  const logWellness = async () => {
    try {
      await axios.post(`${API}/recovery/wellness`, {
        child_id: childId,
        date: new Date().toISOString().split('T')[0],
        ...wellnessData
      });
      toast.success('Wellness logged!');
      fetchData();
      setShowLog(false);
    } catch (err) {
      toast.error('Failed to log wellness');
    }
  };

  const getScoreColor = (s) => {
    if (s >= 85) return 'text-green-500';
    if (s >= 70) return 'text-[#02b5ff]';
    if (s >= 50) return 'text-yellow-500';
    return 'text-[#ff025f]';
  };

  const getScoreIcon = (s) => {
    if (s >= 70) return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (s >= 50) return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    return <AlertTriangle className="h-5 w-5 text-[#ff025f]" />;
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#02b5ff]" />
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid="recovery-tracker">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Battery className="h-5 w-5 text-green-500" />
          Recovery & Wellness
        </h3>
        <Button
          onClick={() => setShowLog(!showLog)}
          size="sm"
          className="bg-zinc-800 hover:bg-zinc-700 text-white rounded-full text-xs"
        >
          {showLog ? 'Hide' : 'Log Today'}
        </Button>
      </div>

      {/* Recovery Score */}
      {score?.recovery && (
        <div className="bg-zinc-800/50 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getScoreIcon(score.recovery.score)}
              <div>
                <div className={`text-3xl font-bold ${getScoreColor(score.recovery.score)}`}>
                  {score.recovery.score}
                </div>
                <div className="text-xs text-zinc-400">{score.recovery.category}</div>
              </div>
            </div>
            <div className="text-right">
              {score.sleep && (
                <div className="flex items-center gap-1 text-sm text-zinc-400">
                  <Moon className="h-3 w-3" />
                  {score.sleep.sleep_duration_hours}h sleep
                </div>
              )}
            </div>
          </div>
          
          {score.recovery.recommendations?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-zinc-700">
              <div className="text-xs text-zinc-400 mb-1">Recommendations</div>
              {score.recovery.recommendations.slice(0, 2).map((rec, i) => (
                <p key={i} className="text-xs text-zinc-300">{rec}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Quick Log Form */}
      {showLog && (
        <div className="bg-zinc-800/30 rounded-lg p-4 mb-4 space-y-4">
          <div>
            <label className="text-xs text-zinc-400 flex items-center gap-1 mb-2">
              <Moon className="h-3 w-3" /> Sleep
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="14"
                  value={sleepData.sleep_duration_hours}
                  onChange={(e) => setSleepData({...sleepData, sleep_duration_hours: parseFloat(e.target.value)})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-sm"
                  placeholder="Hours"
                />
                <span className="text-xs text-zinc-500">Hours</span>
              </div>
              <div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={sleepData.sleep_quality}
                  onChange={(e) => setSleepData({...sleepData, sleep_quality: parseInt(e.target.value)})}
                  className="w-full"
                />
                <span className="text-xs text-zinc-500">Quality: {sleepData.sleep_quality}/10</span>
              </div>
            </div>
            <Button onClick={logSleep} size="sm" className="mt-2 bg-[#02b5ff] text-white text-xs">Log Sleep</Button>
          </div>

          <div className="border-t border-zinc-700 pt-4">
            <label className="text-xs text-zinc-400 flex items-center gap-1 mb-2">
              <Heart className="h-3 w-3" /> Wellness Check
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'energy_level', label: 'Energy', icon: <Sun className="h-3 w-3" /> },
                { key: 'mood', label: 'Mood', icon: <Smile className="h-3 w-3" /> },
                { key: 'muscle_soreness', label: 'Soreness', icon: <Frown className="h-3 w-3" />, invert: true },
                { key: 'stress_level', label: 'Stress', icon: <AlertTriangle className="h-3 w-3" />, invert: true },
                { key: 'hydration', label: 'Hydration', icon: null }
              ].map((item) => (
                <div key={item.key}>
                  <div className="flex items-center gap-1 text-xs text-zinc-400 mb-1">
                    {item.icon} {item.label}: {wellnessData[item.key]}
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={wellnessData[item.key]}
                    onChange={(e) => setWellnessData({...wellnessData, [item.key]: parseInt(e.target.value)})}
                    className="w-full h-1"
                  />
                </div>
              ))}
            </div>
            <Button onClick={logWellness} size="sm" className="mt-3 bg-green-600 text-white text-xs">Log Wellness</Button>
          </div>
        </div>
      )}

      {/* 7-Day History */}
      {history.length > 0 && (
        <div>
          <div className="text-xs text-zinc-400 mb-2 flex items-center gap-1">
            <TrendingUp className="h-3 w-3" /> 7-Day History
          </div>
          <div className="flex gap-1">
            {history.slice(0, 7).reverse().map((day, i) => (
              <div key={i} className="flex-1 text-center">
                <div 
                  className={`h-12 rounded flex items-end justify-center ${
                    day.recovery_score >= 70 ? 'bg-green-500/20' : 
                    day.recovery_score >= 50 ? 'bg-yellow-500/20' : 'bg-red-500/20'
                  }`}
                >
                  <div 
                    className={`w-full rounded-t ${
                      day.recovery_score >= 70 ? 'bg-green-500' : 
                      day.recovery_score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ height: `${day.recovery_score}%` }}
                  />
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">{day.date.split('-')[2]}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
